import React, { useCallback, useEffect } from 'react';
import { useBlocker } from 'react-router';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

interface UseUnsavedChangesWarningProps {
  when: boolean;
  message?: string;
  title?: string;
}

export const useUnsavedChangesWarning = ({
  when,
  message = 'You have unsaved changes. Are you sure you want to leave this page?',
  title = 'Unsaved Changes',
}: UseUnsavedChangesWarningProps) => {
  const shouldBlock = useCallback(() => when, [when]);
  const blocker = useBlocker(shouldBlock);

  // Handle browser refresh/close events
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (when) {
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    if (when) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [when, message]);

  // Render confirmation dialog for navigation attempts
  const ConfirmationDialog = () => {
    if (blocker.state !== 'blocked') {
      return null;
    }

    return (
      <Dialog
        open
        onClose={(event, reason) => {
          // Prevent closing via backdrop click or escape key
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
        }}
        aria-labelledby="unsaved-changes-dialog-title"
        aria-describedby="unsaved-changes-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="unsaved-changes-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningIcon color="warning" />
          {title}
        </DialogTitle>
        <DialogContent>
          <Typography id="unsaved-changes-dialog-description" variant="body1">
            {message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => blocker.reset()} variant="outlined" color="primary" autoFocus>
            Stay on Page
          </Button>
          <Button onClick={() => blocker.proceed()} variant="contained" color="warning">
            Leave Page
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return {
    blocker,
    ConfirmationDialog,
    isBlocked: blocker.state === 'blocked',
    isProceeding: blocker.state === 'proceeding',
  };
};
